import { useEffect, useState } from "react";
import { Table, Spinner, Button, Badge } from "reactstrap";
import { saveAs } from "file-saver";
import { getPadReport, exportPadReportExcel } from "../TenantForm/Helpers/api";

const STATUS_META = {
  Uploaded: { row: "table-success", color: "success" },
  Missing: { row: "table-danger", color: "danger" },
  "N/A": { row: "", color: "secondary" },
};

const WITHIN_PRESETS = [
  { value: null, label: "All units" },
  { value: 30, label: "Updated ≤ 30 days" },
  { value: 60, label: "Updated ≤ 60 days" },
  { value: 90, label: "Updated ≤ 90 days" },
];

const PadUpdatesTab = ({ masterSheetData }) => {
  const mastersheetId = masterSheetData?._id;
  const [within, setWithin] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let active = true;
    if (!mastersheetId) return undefined;
    setLoading(true);
    setError(null);
    getPadReport(mastersheetId, within)
      .then((resp) => {
        if (!active) return;
        if (!resp?.success) throw new Error(resp?.message || "Failed to load report");
        setReport(resp.data);
      })
      .catch((err) => active && setError(err.message || "Failed to load PAD report"))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [mastersheetId, within]);

  const handleExport = async () => {
    setDownloading(true);
    try {
      const blob = await exportPadReportExcel(mastersheetId, within);
      saveAs(new Blob([blob]), `PAD_${masterSheetData?.building_name || "sheet"}.xlsx`);
    } catch (err) {
      console.error("PAD excel export failed:", err);
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return <div className="text-center p-4"><Spinner color="primary" /> Loading PAD report…</div>;
  if (error) return <p className="text-danger p-3">{error}</p>;
  if (!report) return <p className="text-muted p-3">No data available.</p>;

  return (
    <div>
      <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
        {WITHIN_PRESETS.map((p) => (
          <Button
            key={String(p.value)} size="sm"
            color={within === p.value ? "primary" : "outline-primary"}
            onClick={() => setWithin(p.value)}
          >
            {p.label}
          </Button>
        ))}
        <Button size="sm" color="success" className="ms-auto" onClick={handleExport} disabled={downloading}>
          {downloading ? <Spinner size="sm" /> : "Download Excel"}
        </Button>
      </div>

      <div className="d-flex flex-wrap gap-2 mb-3">
        <Badge color="success">Uploaded: {report.totals.uploaded}</Badge>
        <Badge color="danger">Missing: {report.totals.missing}</Badge>
        <Badge color="secondary">N/A: {report.totals.na}</Badge>
        <Badge color="info" className="text-dark">Units: {report.totals.unitsTotal}</Badge>
      </div>

      <Table hover responsive className="table-sm">
        <thead className="table-light">
          <tr>
            <th>Unit #</th><th>Tenant</th><th>Status</th><th>Last Updated</th><th>Age (days)</th><th>Uploaded By</th><th>Versions</th>
          </tr>
        </thead>
        <tbody>
          {report.rows.map((r) => (
            <tr key={r.unitNo} className={STATUS_META[r.status]?.row || ""}>
              <td><strong>{r.unitNo}</strong></td>
              <td>{r.tenant}</td>
              <td><Badge color={STATUS_META[r.status]?.color || "secondary"}>{r.status}</Badge></td>
              <td>{r.lastUpdated ? new Date(r.lastUpdated).toLocaleDateString("en-GB") : "—"}</td>
              <td>{r.ageDays ?? "—"}</td>
              <td>{r.uploadedBy}</td>
              <td>{r.versionCount}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export { PadUpdatesTab };
